import { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes, { func } from 'prop-types';
import { Link } from 'react-router-dom';
import { ENV_VARS } from 'ENV_CONFIG';
import { api, appointmentActions } from '~/api';
import { getDisplayName, getSafety } from '~/utils';
import { Button } from '~/elements';
import {
  errorMessagesActions,
  errorTypes,
  SingleErrorMessage,
  SingleMessage,
  AppointmentTypeNotFoundMessage,
  BeautyToBeautySocietyPartnersNotFound,
} from '../messages';
import { containsMessage } from './helpers';
import { setEditMode } from '../../routes/edit/common/edit-appointment.actions';
import { apiCallFinished, envConfigSelectors } from '~/components';

export const withSingleError = (Component) =>
  class extends PureComponent {
    static propTypes = {
      shopId: PropTypes.string.isRequired,
      hasError: PropTypes.bool.isRequired,
      envConfig: PropTypes.shape({
        services: PropTypes.shape({}).isRequired,
      }).isRequired,
      history: PropTypes.shape({}).isRequired,
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string,
      }).isRequired,
      editMode: PropTypes.bool.isRequired,
      firstEditPagePath: PropTypes.string,
      firstPagePath: PropTypes.string,
      firstPageHash: PropTypes.string,
      appointmentTypeId: PropTypes.string,
      categoryId: PropTypes.string,
      shopShortName: PropTypes.string,
      selectTreatmentPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

      dispatchClearErrorMessages: PropTypes.func.isRequired,
      dispatchTryAgainClicked: PropTypes.func.isRequired,
      dispatchClearAppointment: PropTypes.func.isRequired,
      dispatchSetEditMode: PropTypes.func.isRequired,
      dispatchApiCallFinished: func.isRequired,
    };

    static defaultProps = {
      firstEditPagePath: undefined,
      firstPagePath: '/shops',
      firstPageHash: '',
      appointmentTypeId: undefined,
      categoryId: '',
      shopShortName: '',
      selectTreatmentPath: null,
    };

    static displayName = `WithSingleError(${getDisplayName(Component)})`;

    componentDidUpdate({ location: { pathname: prevPathname } }) {
      const { hasError, location, dispatchClearErrorMessages } = this.props;

      const { pathname } = location;

      if (hasError && pathname !== prevPathname) {
        dispatchClearErrorMessages();
      }
    }

    onClickBookAnAppointment = (event) => {
      event.preventDefault();
      const {
        history,
        firstPagePath,
        firstPageHash,
        appointmentTypeId,
        dispatchSetEditMode,
        envConfig: { services },
      } = this.props;
      dispatchSetEditMode(false);
      if (
        !getSafety(services[api.SERVICE_NAME], `appointmentTypes.${appointmentTypeId}`, undefined)
      ) {
        window.location = `${ENV_VARS.REACT_APP_BROWSE_HOST}${services[api.SERVICE_NAME].landingPageUrl || '/our-services'}`;
      } else {
        history.pushWithToken({
          pathname: firstPagePath,
          hash: firstPageHash,
        });
      }
    };

    onClickBookAnAppointmentWithoutToken = (event) => {
      event.preventDefault();
      const { history, firstPagePath, firstPageHash, dispatchClearErrorMessages } = this.props;
      dispatchClearErrorMessages();
      history.push({
        pathname: firstPagePath,
        hash: firstPageHash,
      });
    };

    onLinkClick = (event) => {
      event.preventDefault();

      const { dispatchTryAgainClicked, dispatchClearErrorMessages } = this.props;

      dispatchClearErrorMessages();
      dispatchTryAgainClicked(true);
    };

    navigateToSelectDate = (event) => {
      event.preventDefault();

      const { dispatchClearAppointment, dispatchSetEditMode, history, shopId } = this.props;

      dispatchClearAppointment(shopId);
      dispatchSetEditMode(false);
      history.pushWithToken('/select-date');
    };

    navigateToShops = (event) => {
      event.preventDefault();

      const { history, editMode, firstEditPagePath, categoryId } = this.props;
      const prevPath = categoryId ? `/treatment-category/${categoryId}` : '/shops';

      history.pushWithToken(editMode ? firstEditPagePath : prevPath);
    };

    navigateToSelectTreatment = (event) => {
      event.preventDefault();

      const { history, selectTreatmentPath, shopShortName } = this.props;

      const path = selectTreatmentPath(shopShortName);
      history.pushWithToken(path);
    };

    render() {
      const {
        location,
        hasError,

        dispatchApiCallFinished,
      } = this.props;

      const { pathname } = location;
      return hasError ? (
        <Fragment>
          <SingleErrorMessage forType={errorTypes.SOMETHING_WENT_WRONG_ERROR}>
            <Link to={pathname} onClick={this.onLinkClick}>
              Try again
            </Link>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.STILL_CANT_LOAD_THE_PAGE_ERROR}>
            <a href={ENV_VARS.REACT_APP_BROWSE_HOST}>Go to homepage</a>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.APPOINTMENT_NO_LONGER_AVAILABLE_ABOUT_YOU_ERROR}>
            <Button view="primary" inline onClick={this.navigateToSelectDate}>
              Choose another time
            </Button>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.APPOINTMENT_CANT_BE_BOOKED_WITHIN_24_HOURS}>
            <a href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/our-services`}>Return to Our services</a>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.NO_AVAILABLE_PARTNERS_ERROR}>
            <Button view="primary" inline onClick={this.navigateToShops}>
              Choose another shop
            </Button>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.VIEW_CANCELLED_BOOKING}>
            <a href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/our-services`}>Our services</a>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.EDIT_CANCELLED_BOOKING}>
            <a href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/our-services`}>Our services</a>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.FORBIDDEN_ERROR}>
            <a href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/our-services`}>Return to Our Services</a>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.TOKEN_INCONSISTENT_DATA_ERROR}>
            <p>
              <a href="#" onClick={this.onClickBookAnAppointment}>
                Book an appointment
              </a>
            </p>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.EMPTY_TOKEN_DATA_ERROR}>
            <p>
              <a href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/our-services`}>Return to Our Services</a>
            </p>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.INVALID_TOKEN_ERROR}>
            <p>
              <a href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/our-services`}>Return to Our Services</a>
            </p>
          </SingleErrorMessage>
          <SingleErrorMessage forType={errorTypes.INVALID_TOKEN_ERROR_GO_TO_BEGIN_OF_JOURNEY}>
            <p>
              <a href="#" onClick={this.onClickBookAnAppointmentWithoutToken}>
                Book an appointment
              </a>
            </p>
          </SingleErrorMessage>
          <SingleMessage forType={errorTypes.NO_AVAILABLE_PARTNERS_GROUPS_ERROR}>
            <Button view="primary" inline onClick={this.navigateToSelectTreatment}>
              Go back to treatments
            </Button>
          </SingleMessage>
          <SingleMessage forType={errorTypes.APPOINTMENT_TYPE_NOT_FOUND}>
            <AppointmentTypeNotFoundMessage
              navigateToSelectTreatment={this.navigateToSelectTreatment}
              dispatchApiCallFinished={dispatchApiCallFinished}
            />
          </SingleMessage>
          <SingleMessage forType={errorTypes.BEAUTY_TO_BEAUTY_SOCIETY_PARTNERS_NOT_FOUND}>
            <BeautyToBeautySocietyPartnersNotFound />
          </SingleMessage>
        </Fragment>
      ) : (
        <Component {...this.props} />
      );
    }
  };

const checkErrors = (errorMessages) =>
  containsMessage(
    errorMessages,
    errorTypes.NO_AVAILABLE_PARTNERS_ERROR,
    errorTypes.SOMETHING_WENT_WRONG_ERROR,
    errorTypes.STILL_CANT_LOAD_THE_PAGE_ERROR,
    errorTypes.APPOINTMENT_NO_LONGER_AVAILABLE_ABOUT_YOU_ERROR,
    errorTypes.APPOINTMENT_CANT_BE_BOOKED_WITHIN_24_HOURS,
    errorTypes.VIEW_CANCELLED_BOOKING,
    errorTypes.EDIT_CANCELLED_BOOKING,
    errorTypes.TOKEN_INCONSISTENT_DATA_ERROR,
    errorTypes.EMPTY_TOKEN_DATA_ERROR,
    errorTypes.FORBIDDEN_ERROR,
    errorTypes.INVALID_TOKEN_ERROR,
    errorTypes.INVALID_TOKEN_ERROR_GO_TO_BEGIN_OF_JOURNEY,
    errorTypes.NO_AVAILABLE_PARTNERS_GROUPS_ERROR,
    errorTypes.APPOINTMENT_TYPE_NOT_FOUND,
    errorTypes.BEAUTY_TO_BEAUTY_SOCIETY_PARTNERS_NOT_FOUND,
  );

export const mapStateToProps = (state) => {
  const {
    messages: {
      app: { errorMessages },
    },
    services: {
      config: { firstPagePath, firstPageHash, selectDatePrevPath: selectTreatmentPath },
    },
    appointment: {
      draft: {
        appointmentType: { id: appointmentTypeId },
        shop: { id: shopId, shortName: shopShortName },
      },
    },
    journeyState: { categoryId },
    editAppointment: { editMode },
    envConfig,
  } = state;
  return {
    hasError: checkErrors(errorMessages),
    shopId,
    editMode,
    firstEditPagePath: envConfigSelectors.selectFirstEditPage(appointmentTypeId)(state),
    firstPagePath,
    firstPageHash,
    appointmentTypeId,
    envConfig,
    categoryId,
    selectTreatmentPath,
    shopShortName,
  };
};

const mapDispatchToProps = {
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchTryAgainClicked: errorMessagesActions.tryAgainClicked,
  dispatchClearAppointment: appointmentActions.deleteAppointment,
  dispatchSetEditMode: setEditMode,
  dispatchApiCallFinished: apiCallFinished,
};

export const ConnectedWithSingleError = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withSingleError(Component));
